import React, { useState, useRef, useEffect } from 'react';
import { auth } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import { updateClientPin, updateClientPinLegacy } from '../../services/PinPadChallengeRepository';

function getDisplayName(profile) {
  return (
    profile?.qualification?.physical?.name ||
    profile?.name ||
    profile?.fullName ||
    profile?.contact?.name ||
    'Perfil'
  );
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── PinInput4 — padrão visual idêntico ao MfaEnrollment ──────────────────────
function PinInput4({ id, value, onChange, disabled }) {
  const refsArr = useRef([]);
  const focus = (i) => refsArr.current[i]?.focus();

  const handleKey = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const next = value.split('');
    next[i] = digit;
    const joined = next.join('').slice(0, 4);
    onChange(joined);
    if (digit && i < 3) setTimeout(() => focus(i + 1), 0);
  };

  const handleBackspace = (i, e) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) focus(i - 1);
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted) { onChange(pasted); setTimeout(() => focus(Math.min(pasted.length - 1, 3)), 0); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-3 justify-center" onPaste={handlePaste}>
      {Array.from({ length: 4 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refsArr.current[i] = el; }}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[i] ? '•' : ''}
          onChange={(e) => handleKey(i, e)}
          onKeyDown={(e) => handleBackspace(i, e)}
          disabled={disabled}
          autoFocus={id === 'pin' && i === 0}
          className={`w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all
            ${value[i]
              ? 'border-[#C5A059] bg-amber-50 text-[#20243B]'
              : 'border-gray-200 bg-gray-50 text-[#20243B] focus:border-[#C5A059] focus:bg-white'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        />
      ))}
    </div>
  );
}

export default function PinPadChallenge({ profile, onSuccess, onBack, onCancel }) {
  const handleBack = onCancel ?? onBack;
  // PASSO 1 — Modo congelado na montagem: imune a re-renders do onSnapshot do Firestore
  const [initialMode] = useState(profile?.securityPin ? 'verify' : 'enroll');
  // LOCKED STATE: congelados na montagem — imunes a re-renders causados por onSnapshot
  const [lockedProfileId] = useState(profile?.id || '');
  const [lockedAppId] = useState(String(FIREBASE_CONFIG?.projectId || '').trim());

  // ── MODO CRIAÇÃO — dois campos simultâneos (padrão MfaEnrollment) ─────────────────
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | success | error

  const showMismatch = pin.length === 4 && confirmPin.length === 4 && pin !== confirmPin;
  const pinMatch    = pin.length === 4 && confirmPin.length === 4 && pin === confirmPin;
  const createReady = pinMatch && saveStatus !== 'saving' && saveStatus !== 'success';
  const isCreateBusy = saveStatus === 'saving' || saveStatus === 'success';

  const handleSavePin = async () => {
    if (!lockedProfileId) {
      // eslint-disable-next-line no-alert
      window.alert('Erro: ID do perfil não mapeado. Volte e selecione o perfil novamente.');
      return;
    }
    if (!createReady) return;
    try {
      setSaveStatus('saving');
      const patch = { securityPin: pin };

      // ATOMICIDADE: Escrita no servidor ANTES de qualquer alterão no estado local.
      // Se o ReBAC rejeitar (403), o erro propaga para o catch externo — sem supressão.
      await updateClientPin(lockedProfileId, pin, lockedAppId);

      // Fallback na coleção raiz (silencioso — pode não existir neste projeto)
      try { await updateClientPinLegacy(lockedProfileId, pin); } catch { void 0; }

      // DOUTRINA ZERO-COPY: sessionStorage só marcado APÓS confirmação do servidor.
      sessionStorage.setItem('mfaVerifiedClient', lockedProfileId);

      // Força renovação do JWT para refletir qualquer Custom Claim injetada pelo servidor.
      try { await getToken(true); } catch { void 0; }

      setSaveStatus('success');
      onSuccess({ ...profile, securityPin: pin });
    } catch (err) {
      console.error('[RDA Auth] Falha crítica ao salvar PIN:', err);
      sessionStorage.removeItem('mfaVerifiedClient');
      if (isMounted.current) setSaveStatus('error');
    }
  };

  // ── MODO VERIFICAÇÃO — 1 campo + auto-submit (comportamento original) ──────
  const [digits, setDigits]       = useState(['', '', '', '']);
  const [verifyError, setVerifyError] = useState('');
  const [verifySaving, setVerifySaving] = useState(false);
  const [shake, setShake]         = useState(false);
  const inputRefs                 = useRef([]);
  const isMounted                 = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (initialMode === 'verify') {
      const t = setTimeout(() => inputRefs.current[0]?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [initialMode]);

  const triggerShake = () =>
 { setShake(true); setTimeout(() => setShake(false), 600); };
  const resetDigits  = () => setDigits(['', '', '', '']);

  const handleVerifyComplete = async (entered) => {
    if (entered === String(profile.securityPin)) {
      try {
        // Força renovação do JWT antes de sinalizar sucesso — alinhado com a pesquisa profunda.
        try { await getToken(true); } catch { void 0; }
        sessionStorage.setItem('mfaVerifiedClient', lockedProfileId);
        if (isMounted.current) onSuccess(profile);
      } catch (err) {
        console.error('[RDA Auth] Falha na renovação do token (verify):', err);
        sessionStorage.removeItem('mfaVerifiedClient');
        if (isMounted.current) {
          setVerifyError('Falha ao renovar sessão. Tente novamente.');
          triggerShake();
          resetDigits();
        }
      }
    } else {
      setVerifyError('PIN incorreto. Tente novamente.');
      triggerShake();
      resetDigits();
      setTimeout(() => inputRefs.current[0]?.focus(), 80);
    }
  };

  const handleDigitChange = (idx, val) => {
    const clean = val.replace(/\D/g, '').slice(-1);
    const next  = [...digits];
    next[idx]   = clean;
    setDigits(next);
    setVerifyError('');
    if (clean) {
      if (idx < 3) {
        inputRefs.current[idx + 1]?.focus();
      } else {
        const entered = next.join('');
        if (entered.length === 4) handleVerifyComplete(entered);
      }
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
      const next = [...digits]; next[idx - 1] = ''; setDigits(next);
    }
  };

  const name = getDisplayName(profile);

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER — MODO CRIAÇÃO (padrão visual MfaEnrollment)
  // ════════════════════════════════════════════════════════════════════════════
  if (initialMode === 'enroll') {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

          {/* ── HEADER ── */}
          <div className="bg-[#20243B] px-6 py-5 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-5"
              style={{ backgroundImage: 'repeating-linear-gradient(45deg,#C5A059 0,#C5A059 1px,transparent 0,transparent 50%)', backgroundSize: '12px 12px' }}
            />
            <div className="relative">
              <h1 className="text-base font-extrabold text-white tracking-wide leading-tight">
                🛡️ Criar PIN de Segurança
              </h1>
              <p className="text-xs font-semibold text-white/80 mt-1.5 leading-relaxed">
                Defina um PIN de 4 dígitos para o perfil{' '}
                <strong className="text-[#C5A059]">{name}</strong>.
              </p>
            </div>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="px-6 py-6 space-y-6">

            {/* ══ BLOCO 1 — NOVO PIN ══ */}
            <div>
              <p className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest mb-3">
                Novo PIN de 4 dígitos:
              </p>
              <PinInput4
                id="pin"
                value={pin}
                onChange={(v) => { setPin(v); if (saveStatus === 'error') setSaveStatus('idle'); }}
                disabled={isCreateBusy}
              />
            </div>

            {/* ══ BLOCO 2 — CONFIRMAR PIN ══ */}
            <div>
              <p className="text-[10px] font-extrabold text-[#20243B] uppercase tracking-widest mb-3">
                Confirmar PIN:
              </p>
              <PinInput4
                id="confirm"
                value={confirmPin}
                onChange={(v) => { setConfirmPin(v); if (saveStatus === 'error') setSaveStatus('idle'); }}
                disabled={isCreateBusy}
              />

              {showMismatch && (
                <p className="mt-2.5 text-xs text-red-600 font-semibold text-center flex items-center justify-center gap-1.5">
                  <i className="fas fa-times-circle" />
                  Os PINs não coincidem. Tente novamente.
                </p>
              )}
              {pinMatch && saveStatus === 'idle' && (
                <p className="mt-2.5 text-xs text-green-700 font-semibold text-center flex items-center justify-center gap-1.5">
                  <i className="fas fa-check-circle" />
                  PINs coincidem — pronto para salvar.
                </p>
              )}
              {saveStatus === 'success' && (
                <p className="mt-2.5 text-xs text-green-700 font-bold text-center flex items-center justify-center gap-1.5">
                  <i className="fas fa-check-circle" />
                  PIN criado! Acessando o perfil...
                </p>
              )}
              {saveStatus === 'error' && (
                <p className="mt-2.5 text-xs text-red-600 font-semibold text-center flex items-center justify-center gap-1.5">
                  <i className="fas fa-exclamation-circle" />
                  Erro ao salvar. Tente novamente.
                </p>
              )}
            </div>

            {/* ══ BOTÕES ══ */}
            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={handleBack}
                disabled={isCreateBusy}
                className="flex-1 py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all border-2"
                style={{
                  background: 'transparent',
                  color: '#C5A059',
                  borderColor: '#C5A059',
                  cursor: isCreateBusy ? 'not-allowed' : 'pointer',
                  opacity: isCreateBusy ? 0.5 : 1,
                }}
              >
                Voltar
              </button>
              <button
                type="submit"
                onClick={handleSavePin}
                disabled={!createReady}
                className="flex-1 py-3 rounded-xl font-extrabold uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2"
                style={{
                  background: createReady ? '#C5A059' : '#e5e7eb',
                  color: createReady ? '#20243B' : '#9ca3af',
                  cursor: createReady ? 'pointer' : 'not-allowed',
                }}
              >
                {saveStatus === 'saving' ? (
                  <>
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-[#20243B]/30 border-t-[#20243B] animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-lock" />
                    Criar PIN
                  </>
                )}
              </button>
            </div>

          </form>

          {/* ══ FOOTER ══ */}
          <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100">
            <p className="text-[11px] font-semibold text-gray-600 text-center leading-relaxed">
              <strong className="text-gray-700">Guarde seu PIN em local seguro.</strong><br />
              Em caso de esquecimento, solicite redefinição ao escritório.
            </p>
          </div>

        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER — MODO VERIFICAÇÃO (Padrão Ouro — mesmo card do enroll, sem avatar)
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">

        {/* ── HEADER ── */}
        <div className="bg-[#20243B] px-6 py-5 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-5"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg,#C5A059 0,#C5A059 1px,transparent 0,transparent 50%)', backgroundSize: '12px 12px' }}
          />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <i className="fas fa-lock text-[#C5A059] text-[11px]" aria-hidden="true" />
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#C5A059]">
                Verificação de Segurança
              </span>
            </div>
            <h1 className="text-base font-extrabold text-white tracking-wide leading-tight uppercase">
              {name}
            </h1>
            <p className="text-xs font-semibold text-white/80 mt-1.5 leading-relaxed">
              Introduza o seu PIN de segurança de 4 dígitos.
            </p>
          </div>
        </div>

        {/* ── PIN BODY ── */}
        <form onSubmit={(e) => e.preventDefault()} className="px-6 py-6 space-y-5">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-400 text-center">
            Digitar PIN
          </p>

          <div className={`flex justify-center gap-3 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}>
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={(el) => (inputRefs.current[idx] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={d}
                disabled={verifySaving}
                onChange={(e) => handleDigitChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                className={
                  'w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all ' +
                  (verifyError
                    ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500'
                    : d
                      ? 'border-[#C5A059] bg-amber-50 text-[#20243B] focus:border-[#C5A059]'
                      : 'border-gray-200 bg-gray-50 text-[#20243B] focus:border-[#C5A059] focus:bg-white')
                }
              />
            ))}
          </div>

          {verifyError && (
            <p className="text-center text-xs font-bold text-red-600">
              <i className="fas fa-exclamation-triangle mr-1" aria-hidden="true" />
              {verifyError}
            </p>
          )}

          {verifySaving && (
            <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-2">
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
              Verificando...
            </p>
          )}
        </form>

        {/* ── FOOTER ── */}
        <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100">
          <button
            type="button"
            onClick={handleBack}
            disabled={verifySaving}
            className="w-full flex items-center justify-center gap-2 py-2 text-[11px] font-bold text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fas fa-arrow-left text-[10px]" aria-hidden="true" />
            Voltar à seleção de perfil
          </button>
        </div>

      </div>
    </div>
  );
}
